import { useFormContext, useWatch } from 'react-hook-form';
import FormMediaFile from 'components/common/forms/FormMediaFile';
import FormCheckbox from 'components/common/forms/FormCheckbox';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import FormAutoHeightTextarea from 'components/common/forms/FormAutoHeightTextarea';
import styles from 'styles/pages/chart/StudyModelChart.module.scss';
import {
  StudyModelChartFields,
  OcclusalTestLabel,
  OcclusalTest,
  MalocclusionClassLabel,
  MalocclusionClass,
} from 'constants/chart/studyModelChart';
import { TAttachmentFiles } from 'types/chart/studyModelChart';
import { TTool } from 'types/apis/chart/studentPracticeChart';
import { TLine } from 'types/chart/pentool';
import useCanvas from 'hooks/chart/useCanvas';
import { Layer, Line, Stage } from 'react-konva';
import useModal from 'hooks/useModal';

type TProps = {
  formMediaFileRefs: React.MutableRefObject<
    Array<{ resetFile: () => void } | null>
  >;
  attachmentFiles: TAttachmentFiles;
  deleteFileIdList: number[];
  setDeleteFileIdList: React.Dispatch<React.SetStateAction<number[]>>;
  changeFile: (index: number, file: File[]) => void;
  tool: TTool;
  strokeWidth: number;
  strokeColor: string;
  strokeActive: boolean;
  lines: TLine[];
  setLines: (lines: TLine[]) => void;
  stageRef: any;
  penLayerRef: any;
  isRegister: boolean;
  isReadOnly: boolean;
};

const StudyModelChart = ({
  formMediaFileRefs,
  attachmentFiles,
  deleteFileIdList,
  setDeleteFileIdList,
  changeFile,
  tool,
  strokeWidth,
  strokeColor,
  strokeActive,
  lines,
  setLines,
  stageRef,
  penLayerRef,
  isRegister,
  isReadOnly,
}: TProps) => {
  const { openModal } = useModal();

  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useCanvas({
    tool,
    strokeWidth,
    strokeColor,
    lines,
    setLines: (newLines) => {
      if (!isRegister || isReadOnly) return;
      setLines(newLines);
    },
  });

  const handleTouchStartWrapper = (e: any) => {
    if (!isRegister || isReadOnly || !strokeActive) return;
    handleTouchStart(e);
  };

  const handleTouchMoveWrapper = (e: any) => {
    if (!isRegister || isReadOnly || !strokeActive) return;
    handleTouchMove(e);
  };

  const handleTouchEndWrapper = (e: any) => {
    if (!isRegister || isReadOnly || !strokeActive) return;
    handleTouchEnd(e);
  };

  const { setValue, register, control } = useFormContext();

  const occlusalTestWatch = useWatch({
    name: StudyModelChartFields.occlusalTest,
    control,
  });
  const overbiteValueWatch = useWatch({
    name: StudyModelChartFields.overbiteValue,
    control,
  });
  const overjetValueWatch = useWatch({
    name: StudyModelChartFields.overjetValue,
    control,
  });

  return (
    <>
      <div className="flex justify_center">
        <dl className={styles.chart_container_top}>
          <dt className={styles.title_wrapper}>상악교합면</dt>
          <dd className={styles.photo_wrapper}>
            <FormMediaFile
              ref={(el) => {
                formMediaFileRefs.current[0] = el as {
                  resetFile: () => void;
                } | null;
              }}
              files={attachmentFiles[0] ? [attachmentFiles[0]] : []}
              inputId="studyModelImages1"
              readOnly={!isRegister || isReadOnly}
              onDelete={(index) =>
                setDeleteFileIdList([...deleteFileIdList, index])
              }
              onChange={(files) => changeFile(0, files)}
            />
          </dd>
        </dl>
      </div>
      <div className="flex justify_center">
        <dl className={styles.chart_container_left}>
          <dt className={styles.title_wrapper}>상·하악교합관계(오른쪽)</dt>
          <dd className={styles.photo_wrapper}>
            <FormMediaFile
              ref={(el) => {
                formMediaFileRefs.current[1] = el as {
                  resetFile: () => void;
                } | null;
              }}
              files={attachmentFiles[1] ? [attachmentFiles[1]] : []}
              inputId="studyModelImages2"
              readOnly={!isRegister || isReadOnly}
              onDelete={(index) =>
                setDeleteFileIdList([...deleteFileIdList, index])
              }
              onChange={(files) => changeFile(1, files)}
            />
          </dd>
        </dl>
        <dl className={styles.chart_container}>
          <dt className={styles.title_wrapper}>상·하악교합관계(전치부)</dt>
          <dd className={styles.photo_wrapper}>
            <FormMediaFile
              ref={(el) => {
                formMediaFileRefs.current[2] = el as {
                  resetFile: () => void;
                } | null;
              }}
              files={attachmentFiles[2] ? [attachmentFiles[2]] : []}
              inputId="studyModelImages3"
              readOnly={!isRegister || isReadOnly}
              onDelete={(index) =>
                setDeleteFileIdList([...deleteFileIdList, index])
              }
              onChange={(files) => changeFile(2, files)}
            />
          </dd>
        </dl>
        <dl className={styles.chart_container_right}>
          <dt className={styles.title_wrapper}>상·하악교합관계(왼쪽)</dt>
          <dd className={styles.photo_wrapper}>
            <FormMediaFile
              ref={(el) => {
                formMediaFileRefs.current[3] = el as {
                  resetFile: () => void;
                } | null;
              }}
              files={attachmentFiles[3] ? [attachmentFiles[3]] : []}
              inputId="studyModelImages4"
              readOnly={!isRegister || isReadOnly}
              onDelete={(index) =>
                setDeleteFileIdList([...deleteFileIdList, index])
              }
              onChange={(files) => changeFile(3, files)}
            />
          </dd>
        </dl>
      </div>
      <div className="flex justify_center">
        <dl className={styles.chart_container_bottom}>
          <dt className={styles.title_wrapper}>하악교합면</dt>
          <dd className={styles.photo_wrapper}>
            <FormMediaFile
              ref={(el) => {
                formMediaFileRefs.current[4] = el as {
                  resetFile: () => void;
                } | null;
              }}
              files={attachmentFiles[4] ? [attachmentFiles[4]] : []}
              inputId="studyModelImages5"
              readOnly={!isRegister || isReadOnly}
              onDelete={(index) =>
                setDeleteFileIdList([...deleteFileIdList, index])
              }
              onChange={(files) => changeFile(4, files)}
            />
          </dd>
        </dl>
      </div>

      <div className="flex justify_center w100 mt48">
        <dl className={styles.text_container_left}>
          <dt className={styles.title_wrapper}>교합 검사</dt>
          <dd className={styles.row}>
            <FormCheckbox
              id={OcclusalTest.Normal}
              name={StudyModelChartFields.occlusalTest}
              text={OcclusalTestLabel[OcclusalTest.Normal]}
              value={OcclusalTest.Normal}
              multiple={true}
              readOnly={!isRegister || isReadOnly}
            />
          </dd>
          <dd className={styles.row}>
            <FormCheckbox
              id={OcclusalTest.Malocclusion}
              name={StudyModelChartFields.occlusalTest}
              text={OcclusalTestLabel[OcclusalTest.Malocclusion]}
              labelStyle={{ marginRight: '24px' }}
              value={OcclusalTest.Malocclusion}
              multiple={true}
              readOnly={!isRegister || isReadOnly}
              afterChange={(value) => {
                const valueAsArray = value as string[];
                if (!valueAsArray.includes(OcclusalTest.Malocclusion)) {
                  setValue(StudyModelChartFields.malocclusionClass, null);
                }
              }}
            />
            <FormRadioBtn
              name={StudyModelChartFields.malocclusionClass}
              labelText={MalocclusionClassLabel[MalocclusionClass.first]}
              value={MalocclusionClass.first}
              disabled={!occlusalTestWatch?.includes(OcclusalTest.Malocclusion)}
              readOnly={!isRegister || isReadOnly}
              labelStyle={{ marginRight: '24px' }}
            />
            <FormRadioBtn
              name={StudyModelChartFields.malocclusionClass}
              labelText={MalocclusionClassLabel[MalocclusionClass.second]}
              value={MalocclusionClass.second}
              disabled={!occlusalTestWatch?.includes(OcclusalTest.Malocclusion)}
              readOnly={!isRegister || isReadOnly}
              labelStyle={{ marginRight: '24px' }}
            />
            <FormRadioBtn
              name={StudyModelChartFields.malocclusionClass}
              labelText={MalocclusionClassLabel[MalocclusionClass.third]}
              value={MalocclusionClass.third}
              disabled={!occlusalTestWatch?.includes(OcclusalTest.Malocclusion)}
              readOnly={!isRegister || isReadOnly}
              labelStyle={{ marginRight: '24px' }}
            />
          </dd>
          <dd className={styles.rowspan}>
            <div className={styles.item}>
              <FormCheckbox
                id={OcclusalTest.Overbite}
                name={StudyModelChartFields.occlusalTest}
                text={OcclusalTestLabel[OcclusalTest.Overbite]}
                value={OcclusalTest.Overbite}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
                afterChange={(value) => {
                  const valueAsArray = value as string[];
                  if (
                    !valueAsArray.includes(OcclusalTest.Overbite) &&
                    overbiteValueWatch
                  ) {
                    openModal({
                      isConfirm: true,
                      title: '작성 중인 내용을 삭제하시겠습니까?',
                      text: '작성 중인 내용이 삭제됩니다.',
                      onCancel: () => {
                        setValue(StudyModelChartFields.occlusalTest, [
                          ...(value as string[]),
                          OcclusalTest.Overbite,
                        ]);
                      },
                      onSubmit: () => {
                        setValue(StudyModelChartFields.overbiteValue, '');
                      },
                    });
                  }
                }}
              />
              <span className="inline_flex">
                (
                <input
                  {...register(StudyModelChartFields.overbiteValue)}
                  type="number"
                  className={styles.input}
                  disabled={!occlusalTestWatch?.includes(OcclusalTest.Overbite)}
                  readOnly={!isRegister || isReadOnly}
                />
                mm)
              </span>
            </div>
            <div className={styles.item}>
              <FormCheckbox
                id={OcclusalTest.Crowding}
                name={StudyModelChartFields.occlusalTest}
                text={OcclusalTestLabel[OcclusalTest.Crowding]}
                value={OcclusalTest.Crowding}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
            </div>
            <div className={styles.item}>
              <FormCheckbox
                id={OcclusalTest.Overjet}
                name={StudyModelChartFields.occlusalTest}
                text={OcclusalTestLabel[OcclusalTest.Overjet]}
                value={OcclusalTest.Overjet}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
                afterChange={(value) => {
                  const valueAsArray = value as string[];
                  if (
                    !valueAsArray.includes(OcclusalTest.Overjet) &&
                    overjetValueWatch
                  ) {
                    openModal({
                      isConfirm: true,
                      title: '작성 중인 내용을 삭제하시겠습니까?',
                      text: '작성 중인 내용이 삭제됩니다.',
                      onCancel: () => {
                        setValue(StudyModelChartFields.occlusalTest, [
                          ...(value as string[]),
                          OcclusalTest.Overjet,
                        ]);
                      },
                      onSubmit: () => {
                        setValue(StudyModelChartFields.overjetValue, '');
                      },
                    });
                  }
                }}
              />
              <span className="inline_flex">
                (
                <input
                  {...register(StudyModelChartFields.overjetValue)}
                  type="number"
                  className={styles.input}
                  disabled={!occlusalTestWatch?.includes(OcclusalTest.Overjet)}
                  readOnly={!isRegister || isReadOnly}
                />
                mm)
              </span>
            </div>
            <div className={styles.item}>
              <FormCheckbox
                id={OcclusalTest.Crossbite}
                name={StudyModelChartFields.occlusalTest}
                text={OcclusalTestLabel[OcclusalTest.Crossbite]}
                value={OcclusalTest.Crossbite}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
            </div>
            <div className={styles.item}>
              <FormCheckbox
                id={OcclusalTest.EdgeToEdgeBite}
                name={StudyModelChartFields.occlusalTest}
                text={OcclusalTestLabel[OcclusalTest.EdgeToEdgeBite]}
                value={OcclusalTest.EdgeToEdgeBite}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
            </div>
            <div className={styles.item}>
              <FormCheckbox
                id={OcclusalTest.Openbit}
                name={StudyModelChartFields.occlusalTest}
                text={OcclusalTestLabel[OcclusalTest.Openbit]}
                value={OcclusalTest.Openbit}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
            </div>
          </dd>
          <dd>
            <Stage
              ref={stageRef}
              width={460}
              height={180}
              className="chart_image_area"
              onTouchStart={handleTouchStartWrapper}
              onTouchMove={handleTouchMoveWrapper}
              onTouchEnd={handleTouchEndWrapper}
            >
              <Layer ref={penLayerRef}>
                {lines.map((line, i) => (
                  <Line
                    key={i}
                    points={line.points}
                    stroke={line.strokeColor}
                    strokeWidth={line.strokeWidth}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation={
                      line.tool === 'eraser' ? 'destination-out' : 'source-over'
                    }
                  />
                ))}
              </Layer>
            </Stage>
          </dd>
        </dl>
        <dl className={styles.text_container_right}>
          <dt className={styles.title_wrapper}>실책 및 보상법</dt>
          <dd className={styles.text_inner_wrapper}>
            <dl className={styles.sub_container_left}>
              <dt className={styles.sub_title_wrapper}>인상채득</dt>
              <dd className={styles.textarea_wrapper}>
                <FormAutoHeightTextarea
                  name="mistakesAndCompensationForImpression"
                  placeholder="텍스트를 입력해 주세요."
                  textareaStyle={{
                    width: '100%',
                    height: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    marginLeft: '0',
                  }}
                  readOnly={!isRegister || isReadOnly}
                />
              </dd>
            </dl>
            <dl className={styles.sub_container_right}>
              <dt className={styles.sub_title_wrapper}>석고 주입</dt>
              <dd className={styles.textarea_wrapper}>
                <FormAutoHeightTextarea
                  name="mistakesAndCompensationForPlaster"
                  placeholder="텍스트를 입력해 주세요."
                  textareaStyle={{
                    width: '100%',
                    height: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    marginLeft: '0',
                  }}
                  readOnly={!isRegister || isReadOnly}
                />
              </dd>
            </dl>
          </dd>
        </dl>
      </div>
    </>
  );
};

export default StudyModelChart;
